import React, { useState } from 'react';
import { DrawerComponent } from 'components/Drawer';
import { ToolTip } from 'components/ToolTip';
import moment from 'moment';
import 'moment/locale/pt-br';
import { customRoundCurrency } from './index';

type AdditionalPackage = {
  id: string | number;
  balance: number;
  expires_at: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  planName: string;
  planValue: number;
  planFrequency: string;
  activationDate: string | undefined;
  planLimit: number;
  balanceContract: number;
};

export const DetalhesDoPlanoDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  planName,
  planValue,
  planFrequency,
  activationDate,
  planLimit,
  balanceContract,
}) => {
  // TODO: substituir pelo dado real da API quando disponível
  const [additionalPackages] = useState<AdditionalPackage[]>([
    { id: 1, balance: 123, expires_at: '2027-04-14' },
    { id: 2, balance: 59, expires_at: '2027-04-06' },
  ]);

  const progressPercent =
    planLimit > 0 ? Math.min(100, (balanceContract / planLimit) * 100) : 0;

  const renewalDate = moment()
    .add(1, 'month')
    .startOf('month')
    .format('D[º] [de] MMMM');

  return (
    <DrawerComponent
      open={isOpen}
      size='default'
      titleClose='Detalhes do plano'
      onCompleteClose={onClose}
    >
      {/* ── Assinatura ── */}
      <section style={{ marginBottom: '24px' }}>
        <p style={{ fontWeight: 700, fontSize: '16px', margin: '16px 0 16px' }}>
          Assinatura
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginBottom: '16px',
          }}
        >
          <div>
            <p style={labelStyle}>Plano</p>
            <p style={valueStyle}>{planName || 'Não informado'}</p>
          </div>
          <div>
            <p style={labelStyle}>Valor</p>
            <p style={valueStyle}>
              {customRoundCurrency(planValue)}{' '}
              <span style={{ color: '#808080', fontSize: '13px' }}>
                /
                {planFrequency === 'mensal'
                  ? 'mês'
                  : planFrequency === 'anual'
                    ? 'ano'
                    : planFrequency || 'mês'}
              </span>
            </p>
          </div>
          <div>
            <p style={labelStyle}>Data de contratação</p>
            <p style={valueStyle}>
              {activationDate
                ? moment(activationDate).format('DD/MM/YYYY')
                : 'Não informado'}
            </p>
          </div>
          <div>
            <p style={labelStyle}>Renovação</p>
            <p style={valueStyle}>{renewalDate}</p>
          </div>
        </div>

        {/* Progress bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '4px',
            marginBottom: '8px',
          }}
        >
          <span style={{ fontSize: '20px', fontWeight: 700 }}>
            {balanceContract.toLocaleString('pt-BR')}
          </span>
          <span style={{ fontSize: '13px', color: '#808080' }}>
            /{planLimit.toLocaleString('pt-BR')}
          </span>
        </div>
        <div
          style={{
            height: '8px',
            borderRadius: '99px',
            background: '#e0e0e0',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${progressPercent}%`,
              borderRadius: '99px',
              background: 'linear-gradient(to right, #4FC3F7, #514180)',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </section>

      <hr style={{ margin: '0 -2rem 24px' }} />

      {/* ── Koins adicionais ── */}
      <section>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px',
          }}
        >
          <p style={{ fontWeight: 700, fontSize: '16px', margin: 0 }}>
            Koins adicionais
          </p>
          <ToolTip
            icon={
              <svg
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <circle
                  cx='8'
                  cy='8'
                  r='6.5'
                  stroke='#1A1A1A'
                  strokeWidth='1.2'
                />
                <line
                  x1='8'
                  y1='5'
                  x2='8'
                  y2='5'
                  stroke='#1A1A1A'
                  strokeWidth='2'
                  strokeLinecap='round'
                />
                <line
                  x1='8'
                  y1='7.5'
                  x2='8'
                  y2='11.5'
                  stroke='#1A1A1A'
                  strokeWidth='1.4'
                  strokeLinecap='round'
                />
              </svg>
            }
            content='Os koins adicionais são utilizados quando o saldo do plano acaba.'
            height='-6.5rem'
          />
        </div>

        {additionalPackages.length === 0 ? (
          <p style={{ color: '#808080', fontSize: '14px' }}>
            Nenhum pacote adicional.
          </p>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              border: '1px solid #e0e0e0',
              borderRadius: '10px',
              padding: '14px 16px',
            }}
          >
            {additionalPackages.map((pkg, index) => (
              <React.Fragment key={pkg.id}>
                {index > 0 && (
                  <hr
                    style={{
                      margin: '4px -16px',
                      border: 'none',
                      borderTop: '1px solid #e0e0e0',
                    }}
                  />
                )}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px',
                  }}
                >
                  <div>
                    <p style={labelStyle}>Saldo disponível</p>
                    <p style={{ ...valueStyle, fontSize: '16px' }}>
                      {pkg.balance.toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <div>
                    <p style={labelStyle}>Vencimento</p>
                    <p style={{ ...valueStyle, fontSize: '16px' }}>
                      {moment(pkg.expires_at).format('DD/MM/YYYY')}
                    </p>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </section>
    </DrawerComponent>
  );
};

const labelStyle: React.CSSProperties = {
  margin: '0 0 0px',
  fontSize: '12px',
  color: '#808080',
};

const valueStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '16px',
  fontWeight: 600,
};
